import React, { useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useToast } from '../../../components/ui/ToastProvider';

export function ParcelRequestDetailsModal({ show, onHide, request, onViewOnMap }) {
  const { show: showToast } = useToast();
  const fields = (request && request.result_fields) ? request.result_fields : {};
  const city = fields.scity || fields.city || '';
  const state = fields.state2 || fields.mail_state2 || '';
  const headline = (request && request.result_headline) || fields.address || '';
  const apn = fields.apn || fields.parcelnumb || fields.account_number || (request && request.parcel_id);
  const owner = fields.owner || '';
  const landval = fields.landval;
  const improvval = fields.improvval;
  const parval = fields.parval;
  const usedesc = fields.usedesc || fields.lbcs_activity_desc;
  const saledate = fields.saledate;
  const saleprice = fields.saleprice;
  const bbox = Array.isArray(request?.geometry_bbox) ? request.geometry_bbox : null;
  // Zoning
  const zoning = fields.zoning || '';
  const zoningType = fields.zoning_type || '';
  const zoningSubtype = fields.zoning_subtype || '';
  const zoningDesc = fields.zoning_description || '';
  const zoningLink = fields.zoning_code_link || '';
  // Buildings
  const yearBuilt = fields.yearbuilt;
  const stories = fields.numstories;
  const bldgCount = fields.ll_bldg_count;
  const bldgFootprintSqft = fields.ll_bldg_footprint_sqft;
  // Risk / Flood
  const femaZone = fields.fema_flood_zone || '';
  const femaZoneSubtype = fields.fema_flood_zone_subtype || '';
  const femaZoneDate = fields.fema_flood_zone_data_date || '';
  const nriRisk = fields.fema_nri_risk_rating || '';
  // Elevation
  const elevLow = fields.lowest_parcel_elevation;
  const elevHigh = fields.highest_parcel_elevation;
  // Acreage strictly from provider fields (no client/server computed guesses)
  const acreage =
    (typeof fields.acres === 'number' && fields.acres) ||
    (typeof fields.area_acres === 'number' && fields.area_acres) ||
    (typeof fields.gisacre === 'number' && fields.gisacre) ||         // County-provided acres (provider data)
    (typeof fields.ll_gisacre === 'number' && fields.ll_gisacre) ||   // Regrid-provided calculated acres (provider data)
    null;

  const summary = useMemo(() => {
    const rows = [];
    if (owner) rows.push(['Owner', owner]);
    if (apn) rows.push(['APN', apn]);
    if (headline) rows.push(['Address', headline]);
    if (city || state) rows.push(['Locality', [city, state].filter(Boolean).join(', ')]);
    // Always show Acreage row; if missing in provider data, show Unavailable
    rows.push(['Acreage', (acreage != null ? `${acreage.toLocaleString()}` : 'Unavailable')]);
    if (typeof landval === 'number') rows.push(['Land value', `$${landval.toLocaleString()}`]);
    if (typeof improvval === 'number') rows.push(['Improvement', `$${improvval.toLocaleString()}`]);
    if (typeof parval === 'number') rows.push(['Assessed total', `$${parval.toLocaleString()}`]);
    if (usedesc) rows.push(['Land use', usedesc]);
    if (saledate) rows.push(['Sale date', saledate]);
    // Show sale price only if > 0; otherwise display Not provided when present as 0 or missing
    if (typeof saleprice === 'number') {
      rows.push(['Sale price', saleprice > 0 ? `$${saleprice.toLocaleString()}` : 'Not provided']);
    } else {
      rows.push(['Sale price', 'Not provided']);
    }
    // Zoning
    if (zoning) rows.push(['Zoning', zoning]);
    if (zoningType) rows.push(['Zoning type', zoningType]);
    if (zoningSubtype) rows.push(['Zoning subtype', zoningSubtype]);
    if (zoningDesc) rows.push(['Zoning description', zoningDesc]);
    if (zoningLink) rows.push(['Zoning code link', zoningLink]);
    // Buildings
    if (typeof yearBuilt === 'number') rows.push(['Year built', `${yearBuilt}`]);
    if (typeof stories === 'number') rows.push(['Stories', `${stories}`]);
    if (typeof bldgCount === 'number') rows.push(['Building count', `${bldgCount}`]);
    if (typeof bldgFootprintSqft === 'number') rows.push(['Building footprint (sqft)', `${bldgFootprintSqft.toLocaleString()}`]);
    // Flood / Risk
    if (femaZone) rows.push(['FEMA flood zone', femaZone]);
    if (femaZoneSubtype) rows.push(['FEMA flood subtype', femaZoneSubtype]);
    if (femaZoneDate) rows.push(['FEMA flood data date', femaZoneDate]);
    if (nriRisk) rows.push(['FEMA NRI risk rating', nriRisk]);
    // Elevation
    if (typeof elevLow === 'number') rows.push(['Lowest parcel elevation', `${elevLow}`]);
    if (typeof elevHigh === 'number') rows.push(['Highest parcel elevation', `${elevHigh}`]);
    return rows;
  }, [
    owner, apn, headline, city, state, acreage, landval, improvval, parval, usedesc,
    saledate, saleprice, zoning, zoningType, zoningSubtype, zoningDesc, zoningLink,
    yearBuilt, stories, bldgCount, bldgFootprintSqft, femaZone, femaZoneSubtype, femaZoneDate,
    nriRisk, elevLow, elevHigh
  ]);

  const handleCopy = async () => {
    try {
      const text = [
        '### Parcel Details',
        ...(summary || []).map(([k, v]) => `- **${k}**: ${String(v)}`)
      ].join('\n');
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        try { showToast && showToast({ title: 'Copied', body: 'Parcel details copied (Markdown)', variant: 'success' }); } catch (_) {}
      } else {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (_) {}
        document.body.removeChild(ta);
        try { showToast && showToast({ title: 'Copied', body: 'Parcel details copied (Markdown)', variant: 'success' }); } catch (_) {}
      }
    } catch (_) {
      try { showToast && showToast({ title: 'Copy failed', body: 'Unable to copy details', variant: 'danger' }); } catch (_) {}
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <span role="img" aria-label="parcel">🗺️</span>
          Parcel Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {summary.length === 0 ? (
          <div className="text-muted">No details returned yet. This request may still be pending.</div>
        ) : (
          <div className="small">
            {summary.map(([k, v]) => (
              <div key={k} className="d-flex justify-content-between border-bottom py-1">
                <div className="text-muted">{k}</div>
                <div className="ms-2 text-end" style={{ maxWidth: '60%' }}>{String(v)}</div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleCopy} aria-label="Copy parcel details">
          Copy
        </Button>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        <Button
          variant="primary"
          onClick={() => onViewOnMap && onViewOnMap(bbox)}
          disabled={!bbox}
          title={!bbox ? 'No map location available' : 'View on map'}
        >
          View on Map
        </Button>
      </Modal.Footer>
    </Modal>
  );
}


